import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';
import { PUBLIC_SURVEY } from '@/constants/testIds';
import { CheckCircle, ArrowRight, ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

function BpAnswer({ answer, setAnswer }) {
    const val = (answer && typeof answer === 'object') ? answer : {};
    const sys = val.systolic ?? '';
    const dia = val.diastolic ?? '';
    const [touched, setTouched] = useState(false);
    const s = Number(sys), d = Number(dia);
    const valid = sys !== '' && dia !== '' && Number.isFinite(s) && Number.isFinite(d)
        && s >= 60 && s <= 250 && d >= 30 && d <= 150 && s > d;
    const show = touched && !valid && (sys !== '' || dia !== '');
    return (
        <div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-sm text-slate-600">Top number</label>
                    <input
                        data-testid={PUBLIC_SURVEY.bpSystolicInput}
                        inputMode="numeric" pattern="[0-9]*"
                        placeholder="120"
                        value={sys}
                        onBlur={() => setTouched(true)}
                        onChange={e => setAnswer({ ...val, systolic: e.target.value === '' ? null : Number(e.target.value) })}
                        className="mt-1 w-full h-24 rounded-2xl border-2 border-slate-300 text-5xl font-medium text-center focus:border-primary focus:outline-none"
                    />
                </div>
                <div>
                    <label className="text-sm text-slate-600">Bottom number</label>
                    <input
                        data-testid={PUBLIC_SURVEY.bpDiastolicInput}
                        inputMode="numeric" pattern="[0-9]*"
                        placeholder="80"
                        value={dia}
                        onBlur={() => setTouched(true)}
                        onChange={e => setAnswer({ ...val, diastolic: e.target.value === '' ? null : Number(e.target.value) })}
                        className="mt-1 w-full h-24 rounded-2xl border-2 border-slate-300 text-5xl font-medium text-center focus:border-primary focus:outline-none"
                    />
                </div>
            </div>
            {show && (
                <div data-testid={PUBLIC_SURVEY.bpError} className="mt-3 text-sm rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3">
                    That doesn&apos;t look right — blood pressure is usually written like 120/80.
                </div>
            )}
        </div>
    );
}

function StepView({ step, answer, setAnswer }) {
    if (step.kind === 'text') {
        return <div className="text-2xl md:text-3xl leading-relaxed text-slate-800 whitespace-pre-wrap">{step.content}</div>;
    }
    if (step.kind === 'video') {
        return (
            <div className="space-y-6">
                <div className="text-2xl md:text-3xl text-slate-800">Please watch this short video:</div>
                <a href={step.content} target="_blank" rel="noreferrer" className="block text-lg text-primary underline break-all">
                    {step.content}
                </a>
            </div>
        );
    }

    // question
    const qt = step.question_type;
    return (
        <div className="space-y-8">
            <div className="text-3xl md:text-4xl font-medium leading-tight text-slate-900">{step.prompt}</div>
            {qt === 'yes-no' && (
                <div className="grid grid-cols-2 gap-4">
                    <button
                        data-testid={PUBLIC_SURVEY.answerYes}
                        onClick={() => setAnswer('Yes')}
                        className={`min-h-[64px] rounded-2xl text-xl font-medium border-2 transition-colors duration-150 ${
                            answer === 'Yes' ? 'bg-primary text-white border-primary' : 'bg-white border-slate-300 text-slate-800 hover:border-primary'
                        }`}
                    >Yes</button>
                    <button
                        data-testid={PUBLIC_SURVEY.answerNo}
                        onClick={() => setAnswer('No')}
                        className={`min-h-[64px] rounded-2xl text-xl font-medium border-2 transition-colors duration-150 ${
                            answer === 'No' ? 'bg-primary text-white border-primary' : 'bg-white border-slate-300 text-slate-800 hover:border-primary'
                        }`}
                    >No</button>
                </div>
            )}
            {qt === 'scale' && (
                <div className="grid grid-cols-6 sm:grid-cols-11 gap-2">
                    {Array.from({ length: 11 }, (_, i) => (
                        <button
                            key={i}
                            data-testid={`${PUBLIC_SURVEY.scaleValue}-${i}`}
                            onClick={() => setAnswer(i)}
                            className={`min-h-[56px] rounded-xl text-lg font-medium border-2 transition-colors duration-150 ${
                                answer === i ? 'bg-primary text-white border-primary' : 'bg-white border-slate-300 text-slate-800 hover:border-primary'
                            }`}
                        >{i}</button>
                    ))}
                </div>
            )}
            {qt === 'number' && (
                <div className="max-w-sm">
                    <div className="flex items-baseline gap-3">
                        <input
                            data-testid={PUBLIC_SURVEY.numberInput}
                            inputMode="decimal"
                            placeholder="0"
                            value={answer ?? ''}
                            onChange={e => setAnswer(e.target.value === '' ? null : Number(e.target.value))}
                            className="w-full h-24 rounded-2xl border-2 border-slate-300 text-5xl font-medium text-center focus:border-primary focus:outline-none"
                        />
                        {step.unit_label && (
                            <span className="text-2xl text-slate-600 whitespace-nowrap">{step.unit_label}</span>
                        )}
                    </div>
                    {(step.number_min != null || step.number_max != null) && (
                        <div className="text-xs text-slate-500 mt-2">
                            Between {step.number_min ?? '—'} and {step.number_max ?? '—'}
                        </div>
                    )}
                </div>
            )}
            {qt === 'blood-pressure' && <BpAnswer answer={answer} setAnswer={setAnswer} />}
            {qt === 'multiple-choice' && (
                <div className="space-y-3">
                    {(step.choices || []).map((c, idx) => (
                        <button
                            key={c}
                            data-testid={`${PUBLIC_SURVEY.choiceOption}-${idx}`}
                            onClick={() => setAnswer(c)}
                            className={`w-full min-h-[64px] rounded-2xl text-xl font-medium border-2 text-left px-6 transition-colors duration-150 ${
                                answer === c ? 'bg-primary text-white border-primary' : 'bg-white border-slate-300 text-slate-800 hover:border-primary'
                            }`}
                        >{c}</button>
                    ))}
                </div>
            )}
            {qt === 'free-text' && (
                <Textarea
                    data-testid={PUBLIC_SURVEY.freeTextInput}
                    rows={5}
                    className="text-lg p-4"
                    value={answer || ''}
                    onChange={e => setAnswer(e.target.value)}
                    placeholder="Type your answer here…"
                />
            )}
        </div>
    );
}

export default function PublicSurvey() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [started, setStarted] = useState(false);
    const [i, setI] = useState(0);
    const [answers, setAnswers] = useState({});
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get(`/public/survey/${token}`).then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || 'Not found'));
    }, [token]);

    if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-slate-600 text-lg">{err}</div>;
    if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Loading…</div>;

    if (data.cancelled || data.status === 'Cancelled') {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div data-testid="public-survey-cancelled" className="max-w-md text-center">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-3xl">·</div>
                    <div className="font-heading text-3xl mt-4 text-slate-900">This check-in is no longer available.</div>
                    <p className="text-slate-600 mt-2 text-lg">Your coordinator cancelled it. If you have questions, give them a call.</p>
                    <div className="mt-8 text-xs text-slate-500 border-t border-slate-200 pt-4 leading-relaxed max-w-sm mx-auto">
                        <b>In an emergency, call 911.</b> Tanow is not for urgent medical help.
                    </div>
                </div>
            </div>
        );
    }

    const steps = data.survey?.steps || [];

    // Filter out steps whose "show_if" isn't satisfied by current answers.
    const visibleSteps = steps.filter(s => {
        const cond = s.show_if;
        if (!cond) return true;
        const parentAns = answers[cond.step_id];
        if (parentAns === undefined || parentAns === null) return false;
        return String(parentAns).toLowerCase() === String(cond.value).toLowerCase();
    });

    // Keep index in range if branches change
    const idx = Math.min(i, Math.max(0, visibleSteps.length - 1));
    const step = visibleSteps[idx];

    const setStepAnswer = (val) => setAnswers(prev => ({ ...prev, [step.step_id]: val }));

    const answerValid = (st, ans) => {
        if (!st || st.kind !== 'question') return true;
        if (ans === undefined || ans === null || ans === '') return false;
        if (st.question_type === 'blood-pressure') {
            if (!ans || typeof ans !== 'object') return false;
            const s = Number(ans.systolic), d = Number(ans.diastolic);
            return Number.isFinite(s) && Number.isFinite(d) && s >= 60 && s <= 250 && d >= 30 && d <= 150 && s > d;
        }
        if (st.question_type === 'number') {
            const n = Number(ans);
            if (!Number.isFinite(n)) return false;
            if (st.number_min != null && n < st.number_min) return false;
            if (st.number_max != null && n > st.number_max) return false;
            return true;
        }
        return true;
    };
    const canAdvance = answerValid(step, answers[step?.step_id]);

    const submit = async () => {
        // Only send responses for currently-visible questions (respects show_if)
        const visIds = new Set(visibleSteps.filter(s => s.kind === 'question').map(s => s.step_id));
        const responses = Array.from(visIds).map(sid => ({ step_id: sid, answer: answers[sid] ?? null }));
        try {
            await api.post(`/public/survey/${token}/submit`, { responses });
            setDone(true);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Submission failed');
        }
    };

    if (data.status !== 'Sent' && !done) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div data-testid={PUBLIC_SURVEY.thankYou} className="max-w-md text-center">
                    <CheckCircle size={56} weight="duotone" className="mx-auto text-emerald-600" />
                    <div className="font-heading text-3xl mt-4 text-slate-900">All done.</div>
                    <p className="text-slate-600 mt-2 text-lg">This check-in has already been sent in. Thank you.</p>
                    <div className="mt-8 text-xs text-slate-500 border-t border-slate-200 pt-4 leading-relaxed max-w-sm mx-auto">
                        <b>In an emergency, call 911.</b> Tanow is not for urgent medical help.
                    </div>
                </div>
            </div>
        );
    }

    if (done) {
        const back = new URLSearchParams(window.location.search).get('back');
        if (back && /^\/board\/[a-zA-Z0-9_-]+$/.test(back)) {
            // Auto-redirect back to the board so the card disappears on next poll.
            setTimeout(() => { window.location.href = back; }, 2500);
        }
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div data-testid={PUBLIC_SURVEY.thankYou} className="max-w-md text-center">
                    <CheckCircle size={64} weight="duotone" className="mx-auto text-emerald-600" />
                    <div className="font-heading text-4xl mt-4 text-slate-900">Thank you.</div>
                    <p className="text-slate-700 mt-3 text-xl leading-relaxed">
                        Your answers have been sent to the family.
                    </p>
                    {back && (
                        <a
                            data-testid="public-survey-back-to-board"
                            href={back}
                            className="mt-6 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground px-6 py-3 text-lg font-medium"
                        >
                            Back to the board →
                        </a>
                    )}
                    <div className="mt-10 text-xs text-slate-500 border-t border-slate-200 pt-4 leading-relaxed max-w-sm mx-auto">
                        <b>In an emergency, call 911.</b> Tanow is not for urgent medical help.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid={PUBLIC_SURVEY.root} className="min-h-screen bg-background flex flex-col">

            {!started ? (
                <div className="flex-1 flex items-center justify-center px-6 py-10">
                    <div className="max-w-lg w-full text-center">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Check-in for</div>
                        <div className="font-heading text-4xl md:text-5xl text-slate-900 mt-2">{data.patient_name}</div>
                        <div className="font-heading text-2xl text-primary mt-6">{data.survey?.name}</div>
                        {data.survey?.description && (
                            <p className="text-slate-600 text-lg mt-4 leading-relaxed">{data.survey.description}</p>
                        )}
                        <div className="text-slate-500 text-sm mt-6">{steps.length} step{steps.length === 1 ? '' : 's'} · takes about {Math.max(1, Math.round(steps.length * 0.5))} min</div>
                        <Button
                            data-testid={PUBLIC_SURVEY.startButton}
                            onClick={() => setStarted(true)}
                            className="mt-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-10 text-lg"
                        >
                            Begin <ArrowRight size={18} className="ml-2" />
                        </Button>
                        <div className="mt-10 text-xs text-slate-500 border-t border-slate-200 pt-4 leading-relaxed">
                            <b>In an emergency, call 911.</b> Tanow is not for urgent medical help.
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1 flex flex-col">
                    <div className="max-w-lg mx-auto w-full px-6 pt-8 pb-6">
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-primary transition-all duration-300" style={{ width: `${((idx + 1) / Math.max(1, visibleSteps.length)) * 100}%` }} />
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mt-3">Step {idx + 1} of {visibleSteps.length}</div>
                    </div>

                    <div className="flex-1 max-w-lg mx-auto w-full px-6 py-6">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={step?.step_id}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                transition={{ duration: 0.25 }}
                            >
                                <StepView step={step} answer={answers[step.step_id]} setAnswer={setStepAnswer} />
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="border-t border-slate-200 bg-white/70 backdrop-blur">
                        <div className="max-w-lg mx-auto w-full px-6 py-4 flex items-center gap-3">
                            <Button
                                data-testid={PUBLIC_SURVEY.prevButton}
                                variant="outline" className="rounded-full h-12 px-5"
                                disabled={idx === 0}
                                onClick={() => setI(x => Math.max(0, x - 1))}
                            >
                                <ArrowLeft size={16} className="mr-1" /> Back
                            </Button>
                            {idx < visibleSteps.length - 1 ? (
                                <Button
                                    data-testid={PUBLIC_SURVEY.nextButton}
                                    className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 flex-1"
                                    disabled={!canAdvance}
                                    onClick={() => setI(x => Math.min(visibleSteps.length - 1, x + 1))}
                                >
                                    Continue <ArrowRight size={16} className="ml-2" />
                                </Button>
                            ) : (
                                <Button
                                    data-testid={PUBLIC_SURVEY.submitButton}
                                    className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 flex-1"
                                    disabled={!canAdvance}
                                    onClick={submit}
                                >
                                    Send answers
                                </Button>
                            )}
                        </div>
                        <div className="max-w-lg mx-auto w-full px-6 pb-4 text-[11px] text-slate-500 leading-relaxed">
                            <b>In an emergency, call 911.</b> Tanow is not for urgent medical help.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
